/** Defines the breakpoints used for responsive attributes. */
export type Breakpoint = 'default' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
/** All breakpoints possible */
export const BREAKPOINTS: Breakpoint[] = ['default', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

/** A model wich defines a attribute for each breakpoint */
export type ResponsiveAttribute<T> = Partial<Record<Breakpoint, T>> & { default: T };

/** A value that is either fixed for every breakpoint or set per breakpoint. */
export type MaybeResponsive<T> = T | ResponsiveAttribute<T>;
/** Type guard to check if an attribute is a ResponsiveAttribute */
export function isResponsiveAttribute<T>(attr: unknown): attr is ResponsiveAttribute<T> {
  return typeof attr === 'object' && attr !== null && 'default' in attr;
}

/**
 * Breakpoint min-widths in px. MUST mirror the `screens` in tailwind.config.js, since these drive the
 * JS device-size resolution (`StyleService.updateDeviceSize`, `getCurrentResponsiveValue`), the
 * responsive `sizes` attribute and the `@container` blocks `build-template.ts` generates into
 * `responsive-values.css` — all of which have to agree with the breakpoints the rendered `md:`/`lg:`
 * classes use.
 */
export const TAILWIND_BREAKPOINTS: Record<Breakpoint, number> = {
  default: 0,
  xs: 375,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400
};

/**
 * Resolves the breakpoint Tailwind applies at a given width: the highest breakpoint whose min-width
 * is less than or equal to the width (mobile-first).
 * @param width The width in px
 * @returns The matching breakpoint key
 */
export function getBreakpointForWidth(width: number): Breakpoint {
  return [...BREAKPOINTS].reverse().find((breakpoint) => width >= TAILWIND_BREAKPOINTS[breakpoint]) ?? 'default';
}

/**
 * The values `responsive-values.css` resolves per breakpoint, keyed by the name used in
 * `[gsResponsive]`. `property` is the CSS property the class declares, `neutral` the value written to
 * `--gs-r-{key}-default` when the configuration starts at a higher breakpoint.
 *
 * Every fallback chain in the generated CSS terminates in the `default` variable. Without it the
 * declaration would be invalid at computed-value time, still win the cascade and resolve to `unset` —
 * silently dropping whatever rule applied before. The neutral values are therefore the properties'
 * own initial values (`inherit` for the inherited ones), so below the lowest configured breakpoint
 * the element keeps the value it would have without a responsive configuration.
 *
 * Adding an entry is enough: `build-template.ts` regenerates the rules in `responsive-values.css`
 * from this map, and `StyleService.getResponsiveStyle()` needs a formatter only for value shapes it
 * does not already handle (number, boolean, `ISides`).
 *
 * `display` and `visible` both declare `display`. They belong on separate elements — on the same one
 * the later entry wins, which is `visible`.
 */
export const RESPONSIVE_STYLE_PROPERTIES = {
  padding: { property: 'padding', neutral: '0px' },
  'padding-inline': { property: 'padding-inline', neutral: '0px' },
  'font-size': { property: 'font-size', neutral: 'inherit' },
  'max-width': { property: 'max-width', neutral: 'none' },
  height: { property: 'height', neutral: 'auto' },
  'max-height': { property: 'max-height', neutral: 'none' },
  'min-height': { property: 'min-height', neutral: '0px' },
  display: { property: 'display', neutral: 'block' },
  'align-items': { property: 'align-items', neutral: 'normal' },
  visible: { property: 'display', neutral: 'none' }
} as const;

/** A value that can be resolved per breakpoint by `responsive-values.css`. */
export type ResponsiveStyleProperty = keyof typeof RESPONSIVE_STYLE_PROPERTIES;
